import { Box, Typography, Button, useTheme } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useImpersonation } from "../contexts/ImpersonationContext";

/**
 * Persistent top banner shown when admin is acting as another org.
 * Hidden in all other cases.
 */
export default function ImpersonationBanner() {
  const theme = useTheme();
  const { isImpersonating, actingOrgId, actingOrg, actingUserId, actingUser, clearActing } = useImpersonation();

  if (!isImpersonating) return null;

  const exit = () => {
    clearActing();
    window.location.reload();
  };

  // When viewing a specific user, show who + the write mode (writes to a real customer are
  // rejected server-side; only flagged test accounts are read-write).
  const isTestTarget = !!(actingUser && (actingUser.is_test || actingUser.is_test_user));
  const mode = isTestTarget ? "test account · read-write" : "read-only";

  return (
    <Box
      sx={{
        bgcolor: theme.palette.warning.main,
        color: "#fff",
        px: 2,
        py: 0.75,
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: 13,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        position: "sticky",
        top: 0,
        zIndex: 1100,
      }}
    >
      <VisibilityIcon fontSize="small" />
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
        {/* Every label degrades to the raw id. The banner is the only signal that acting
            headers are being sent, so it must render even when the org/user lookups failed —
            an unlabelled banner is recoverable, an invisible one is not. */}
        {actingUserId ? (
          <>
            Viewing as <strong>{actingUser?.email || actingUser?.display_name || actingUserId}</strong>
            <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.85 }}>
              ({actingOrg?.domain || actingOrgId} · {mode})
            </Typography>
          </>
        ) : (
          <>
            Viewing as <strong>{actingOrg?.domain || actingOrgId}</strong>
            <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.85 }}>
              ({actingOrg?.org_id || actingOrgId})
            </Typography>
          </>
        )}
      </Typography>
      <Button
        size="small"
        onClick={exit}
        sx={{
          color: "#fff",
          borderColor: "rgba(255,255,255,0.6)",
          textTransform: "none",
          fontSize: 12,
          "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
        }}
        variant="outlined"
      >
        Exit
      </Button>
    </Box>
  );
}
